import { useMemo, useState } from "react";
import { Badge } from "flowbite-react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { formatToReal } from "../../../utils/utils";

const PendenciasFinanceiras = ({
  pendenciasFinanceiras = [],
  quantidadeTotal,
  dataMaiorPendencia,
  valorTotal,
  temDataDisponibilidade = true,
  temContrato = true,
}) => {
  const [expandido, setExpandido] = useState(false);

  const hasContrato =
    pendenciasFinanceiras.length > 0 &&
    pendenciasFinanceiras.every((p) =>
      Object.prototype.hasOwnProperty.call(p, "contrato")
    );

  const LIMITE = 12;
  const total = pendenciasFinanceiras.length;
  const restantes = Math.max(total - LIMITE, 0);

  const listaExibida = useMemo(
    () =>
      expandido
        ? pendenciasFinanceiras
        : pendenciasFinanceiras.slice(0, LIMITE),
    [expandido, pendenciasFinanceiras]
  );

  let classGrid = 6;

  if (temDataDisponibilidade) classGrid += 1;

  if (hasContrato || temContrato) classGrid += 1;

  const gridColsClass =
    {
      6: "grid-cols-6",
      7: "grid-cols-7",
      8: "grid-cols-8",
    }[classGrid] || "grid-cols-6";

  return (
    <>
      {/* Resumo */}
      <div className="grid gap-3 mb-5 grid-cols-3">
        {quantidadeTotal && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Quantidade total
            </div>
            <div className="text-lg font-semibold text-slate-900">
              {quantidadeTotal}
            </div>
          </div>
        )}

        {dataMaiorPendencia && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Data maior
            </div>
            <div className="text-sm font-semibold text-slate-900">
              {dataMaiorPendencia}
            </div>
          </div>
        )}

        {valorTotal && (
          <div className="flex items-center justify-between rounded-2xl border border-red-200 bg-red-50 px-4 py-1 text-sm shadow-sm">
            <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
              Valor total
            </div>
            <div className="text-lg font-semibold text-rose-600">
              {formatToReal(valorTotal)}
            </div>
          </div>
        )}
      </div>

      {/* Tabela + Fade quando colapsado */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div
            className={`grid ${gridColsClass} gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500`}
          >
            <div className="text-left">Data</div>
            {temDataDisponibilidade && (
              <div className="text-left">Disponib.</div>
            )}

            <div className="col-span-2 text-left">Informante</div>
            <div className="text-left">Cidade</div>
            <div className="text-left">Tipo</div>
            {(hasContrato || temContrato) && (
              <div className="text-left">Contrato</div>
            )}
            <div className="text-right">Valor</div>
          </div>
        </div>

        {/* Área rolável quando colapsado, expandida quando aberto */}
        <div
          className={`transition-all duration-300 ease-out ${
            expandido ? "max-h-[9999px]" : "max-h-auto"
          } overflow-hidden`}
        >
          <div className="divide-y divide-slate-100">
            {listaExibida.map((pendencia, index) => (
              <div
                key={index}
                className={`grid ${gridColsClass} items-center gap-2 px-4 py-3 text-sm transition-colors ${
                  index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
                } hover:bg-slate-50`}
              >
                <div className="font-medium text-slate-700">
                  {pendencia?.data || "-"}
                </div>
                {temDataDisponibilidade && (
                  <div className="text-slate-600">
                    {pendencia?.dataDisponibilidade || "-"}
                  </div>
                )}

                <div
                  className="col-span-2 font-medium text-slate-900 truncate text-xs uppercase"
                  title={pendencia?.informante || ""}
                >
                  {pendencia?.informante || "-"}
                </div>

                <div className="text-slate-700">{pendencia?.cidade || "-"}</div>
                <div className="text-slate-700">
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">
                    {pendencia?.tipoPendencia || "—"}
                  </span>
                </div>
                {(hasContrato || temContrato) && (
                  <div className="min-w-0 text-slate-700">
                    <span
                      className="block max-w-full truncate rounded-md border border-slate-200 bg-slate-50 px-1.5 text-[11px] font-medium text-slate-700"
                      title={pendencia?.contrato || ""}
                    >
                      {pendencia?.contrato || "-"}
                    </span>
                  </div>
                )}
                <div className="text-right font-semibold text-rose-600 tabular-nums">
                  {formatToReal(pendencia?.valor)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fade de “tem mais” quando colapsado */}
        {!expandido && total > LIMITE && (
          <div className="pointer-events-none absolute inset-x-0 bottom-[52px] h-16 bg-gradient-to-t from-white to-white/0" />
        )}

        {/* Footer elegante */}
        {total > LIMITE && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3">
            <button
              type="button"
              onClick={() => setExpandido((v) => !v)}
              aria-expanded={expandido}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              {expandido ? (
                <>
                  <HiChevronUp className="text-lg" />
                  Ver menos
                </>
              ) : (
                <>
                  <HiChevronDown className="text-lg" />
                  Ver mais
                </>
              )}
            </button>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Badge color="gray">
                Exibindo {Math.min(LIMITE, total)} de {total}
              </Badge>
              {!expandido && restantes > 0 && (
                <span className="text-slate-500">(+{restantes} restantes)</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Aviso quando colapsado */}
      {!expandido && total > LIMITE && (
        <div className="text-sm mt-3 text-gray-500 text-center">
          Exibindo as últimas {LIMITE} ocorrências.
        </div>
      )}
    </>
  );
};

export default PendenciasFinanceiras;
